import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import debounce from "lodash/debounce";
import styled, { css } from "styled-components";
import { Input, ListGroup, ListGroupItem } from "components/bootstrap";
import NoSearchResult from "components/common/NoSearchResult";
import useIsKeyHeld from "hooks/useIsKeyHeld";
import Spinner from "components/common/Spinner";
import PaginatedList from "components/common/PaginatedList";
const Container = styled.div(
  ({ theme }) => css`
    color: ${theme.colors.text.primary};
    padding: ${theme.spacings.xs} 0;
  `
);
const sidePaddings = ({ theme }) => css`
  padding: 0 ${theme.spacings.sm};
`;
const PaddingContainer = styled.div(sidePaddings);
const SearchInput = styled(Input)`
  margin-bottom: 6px;
`;
const ScrollableArea = styled.div`
  max-height: 50vh;
  overflow: auto;
`;
const StyledListGroup = styled(ListGroup)`
  margin-bottom: 0;
`;
const Hint = styled.div(
  ({ theme }) => css`
    margin-top: 9px;
    font-size: ${theme.fonts.size.small};
  `
);
const StyledPaginatedList = styled(PaginatedList)(sidePaddings);
const SuggestionsList = ({
  allActiveFilters,
  attribute,
  filterValueRenderer,
  isLoading,
  multiSelect,
  onSubmit,
  page,
  pageSize,
  setSearchParams,
  suggestions,
  total
}) => {
  const { t } = useTranslation("common");
  const isShiftHeld = useIsKeyHeld("Shift");
  const handleSearchChange = useCallback(
    (newSearchQuery) => {
      setSearchParams((cur) => ({ ...cur, page: 1, query: newSearchQuery }));
    },
    [setSearchParams]
  );
  const handlePaginationChange = useCallback(
    (newPage) => {
      setSearchParams((cur) => ({ ...cur, page: newPage }));
    },
    [setSearchParams]
  );
  const debounceOnSearch = debounce((value) => handleSearchChange(value), 1e3);
  return /* @__PURE__ */ React.createElement(Container, null, /* @__PURE__ */ React.createElement(PaddingContainer, null, /* @__PURE__ */ React.createElement(
    SearchInput,
    {
      type: "text",
      id: "search-filters-input",
      formGroupClassName: "",
      placeholder: t("filters.search_attribute", { attribute: attribute.title }),
      onChange: ({ target: { value } }) => debounceOnSearch(value)
    }
  ), isLoading && /* @__PURE__ */ React.createElement(Spinner, null)), !!(suggestions == null ? void 0 : suggestions.length) && /* @__PURE__ */ React.createElement(
    StyledPaginatedList,
    {
      showPageSizeSelect: false,
      totalItems: total,
      hidePreviousAndNextPageLinks: true,
      hideFirstAndLastPageLinks: true,
      activePage: page,
      pageSize,
      onChange: handlePaginationChange,
      useQueryParameter: false
    },
    /* @__PURE__ */ React.createElement(ScrollableArea, null, /* @__PURE__ */ React.createElement(PaddingContainer, null, /* @__PURE__ */ React.createElement(StyledListGroup, null, suggestions.map((suggestion) => {
      var _a;
      const filterValue = suggestion.target_id || suggestion.id;
      const disabled = !!((_a = allActiveFilters == null ? void 0 : allActiveFilters.get(attribute.id)) == null ? void 0 : _a.find(({ value }) => value === filterValue));
      const onClick = () => {
        if (disabled) {
          return;
        }
        onSubmit(
          {
            value: filterValue,
            title: suggestion.value
          },
          !multiSelect ? true : !isShiftHeld
        );
      };
      return /* @__PURE__ */ React.createElement(ListGroupItem, { onClick, key: `filter-value-${suggestion.id}`, disabled }, filterValueRenderer ? filterValueRenderer(suggestion.id, suggestion.value) : suggestion.value);
    }))))
  ), /* @__PURE__ */ React.createElement(PaddingContainer, null, !(suggestions == null ? void 0 : suggestions.length) && /* @__PURE__ */ React.createElement(NoSearchResult, null, t("filters.no_entities_found")), multiSelect && /* @__PURE__ */ React.createElement(Hint, null, /* @__PURE__ */ React.createElement("i", null, t("filters.hold_shift")))));
};
export default SuggestionsList;
